/**
 * <sticky-buy-bar> - PDP-koeb-bar (BUILD_PLAN 4.4). Viser sig naar den inline
 * buy-box ([data-sticky-watch], fallback: produktformen i main-product) har
 * forladt viewporten OPAD, og skjules igen naar bundens stop-sentinel
 * ([data-sticky-stop], ellers .footer) er i sigte.
 *
 * Progressive enhancement: uden JS forbliver baren skjult (ren CSS-default).
 * CTA'en submitter den RIGTIGE produktform (requestSubmit), saa valgt
 * variant/antal altid foelger formen, og cart-drawer-interceptoren tager
 * over derfra. IntersectionObservers ryddes op i disconnectedCallback;
 * elementet reconnectes selv ved shopify:section:load.
 *
 * Stoerrelseslisten (9.28, 2026-09-25) er en modal <dialog> (showModal ->
 * native fokusfaelde, Escape og inert baggrund). Et valg sendes til buy-boxens
 * <variant-picker> via selectVariant(), saa selecten forbliver eneste
 * add-to-cart-id. Baren opdaterer sig selv paa variant:change ved at hente sin
 * egen section med ?variant= - stoerrelse, pris og CTA kommer fra serveren.
 * Er baren skjult (kunden klikker i selve buy-boxen), udskydes hentningen til
 * baren vises igen - ellers kostede hvert chip-klik to section-renders.
 *
 * Halvt bukse-valg (livvidde uden laengde) sender variant:change med id: null
 * (pickerens kontrakt, se #broadcast), og #onVariantChange frasorterer et
 * falsy id - saa baren viser stadig den forrige hele stoerrelse. Bevidst: CTA-vagten i
 * #onClick aabner listen i stedet for at submitte, saa det kan aldrig give en
 * forkert kurv-linje.
 */
import { fetchSection, abortable } from '@theme/section-render';

const SECTION = '[id^="shopify-section-"]';

class StickyBuyBar extends HTMLElement {
  #productForm() {
    return document.querySelector("[data-main-product] form[action*='/cart/add']");
  }

  #picker() {
    return document.querySelector('[data-main-product] variant-picker');
  }

  connectedCallback() {
    this.dialog = this.querySelector('[data-sticky-sizes]');
    this.toggle = this.querySelector('[data-sticky-size-toggle]');
    this.staleId = null;
    this.addAfterPick = false;
    this.nextSignal = abortable();

    this.addEventListener('click', this.#onClick);
    document.addEventListener('variant:change', this.#onVariantChange);

    if (this.dialog) {
      this.dialog.addEventListener('click', this.#onBackdrop);
      this.dialog.addEventListener('close', this.#onClose);
    }

    this.watch =
      document.querySelector('[data-sticky-watch]') || this.#productForm() || document.querySelector('[data-hero]');
    if (!this.watch || !('IntersectionObserver' in window)) return;

    this.pastWatch = false;
    this.atEnd = false;

    this.watchIo = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        this.pastWatch = e.isIntersecting === false && e.boundingClientRect.top < 0;
        this.sync();
      },
      { threshold: 0 },
    );
    this.watchIo.observe(this.watch);

    // Bundens stop: skjul baren naar footeren er i sigte.
    this.stop = document.querySelector('[data-sticky-stop]') || document.querySelector('.footer');
    if (this.stop) {
      this.stopIo = new IntersectionObserver(
        (entries) => {
          this.atEnd = entries[0].isIntersecting;
          this.sync();
        },
        { threshold: 0 },
      );
      this.stopIo.observe(this.stop);
    }
  }

  // Klik paa backdrop'en (selve dialog-elementet, uden for indholdet) lukker.
  #onBackdrop = (event) => {
    if (event.target === this.dialog) this.dialog.close();
  };

  // Fokus tilbage til STOERRELSES-knappen, ogsaa naar listen blev aabnet fra
  // CTA'en: kunden har lige valgt en stoerrelse, og knappen viser nu valget.
  #onClose = () => {
    this.addAfterPick = false;
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-expanded', 'false');
    if (document.contains(this.toggle)) this.toggle.focus();
  };

  #onClick = (event) => {
    if (event.target.closest('[data-sticky-size-toggle]')) {
      this.#open();
      return;
    }
    if (event.target.closest('[data-sticky-sizes-close]')) {
      this.dialog.close();
      return;
    }
    const row = event.target.closest('[data-sticky-variant]');
    if (row) {
      this.#select(row);
      return;
    }
    const add = event.target.closest('[data-sticky-add]');
    if (!add) return;
    const form = this.#productForm();
    if (!form) return;
    // Listen i stedet for kurven, i to tilfaelde - og valget laegger derefter
    // varen i kurven, for kunden har allerede trykket ADD TO CART:
    // 1. Kunden har ikke selv valgt en stoerrelse endnu. Selecten peger paa
    //    buy-boxens forvalg, som baren netop ikke udgiver for et valg.
    // 2. Buy-boxens knap er deaktiveret, fordi bukse-valget kun er halvt
    //    (livvidde uden laengde); selecten peger saa paa den FORRIGE variant.
    const submit = form.querySelector('[data-add-button] button');
    if (this.dialog && (!this.hasAttribute('data-size-chosen') || (submit && submit.disabled))) {
      this.#open();
      this.addAfterPick = true;
      return;
    }
    form.requestSubmit();
  };

  #open() {
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    if (!this.dialog.open) this.dialog.showModal();
    if (this.toggle) this.toggle.setAttribute('aria-expanded', 'true');
    // Fokus paa den valgte stoerrelse - eller, foer et valg, den foerste
    // tilgaengelige - ikke luk-krydset, saa tastatur- og skaermlaeserbrugere
    // starter i listen.
    const first =
      this.dialog.querySelector('[data-sticky-variant][aria-current="true"]:not(:disabled)') ||
      this.dialog.querySelector('[data-sticky-variant]:not(:disabled)');
    if (first) first.focus();
  }

  #select(row) {
    if (row.disabled) return;
    const addAfterPick = this.addAfterPick;
    const picker = this.#picker();
    const ok =
      picker && typeof picker.selectVariant === 'function'
        ? picker.selectVariant(row.dataset.stickyVariant, { scroll: false })
        : false;
    this.dialog.close();
    // selectVariant saetter selecten synkront, saa formen baerer allerede
    // den valgte variant; cart-drawer-interceptoren tager den derfra.
    if (addAfterPick && ok) {
      const form = this.#productForm();
      if (form) form.requestSubmit();
    }
  }

  // Kun buy-boxens picker driver baren. En picker med data-render-url staar i
  // quick-view (PLP-promo eller complementary-blokken) paa en ANDEN vare.
  #onVariantChange = (event) => {
    const picker = event.target;
    if (!(picker instanceof HTMLElement) || picker.dataset.renderUrl) return;
    if (!picker.closest('[data-main-product]')) return;
    const id = event.detail && event.detail.id;
    if (!id) return;
    // Et variantskift fra buy-boxens picker ER et aktivt valg (klik i buy-
    // boxen eller i listen) - forvalget ved sideload sender intet event.
    this.setAttribute('data-size-chosen', '');
    // Listen og stoerrelses-labelen foelger straks (identitet, ingen penge);
    // pris og CTA fra serveren. Uden det stod forvalgets titel i labelen
    // indtil svaret landede.
    this.querySelectorAll('[data-sticky-variant]').forEach((row) => {
      const match = row.dataset.stickyVariant === String(id);
      if (match) row.setAttribute('aria-current', 'true');
      else row.removeAttribute('aria-current');
      if (match) this.#setSizeLabel(row);
    });
    this.staleId = String(id);
    if (this.classList.contains('is-visible')) this.#refresh();
  };

  // Radens foerste tekstnode er variantens titel (sub-labelen er et span).
  #setSizeLabel(row) {
    const label = this.querySelector('[data-sticky-size-value]');
    const value = row.querySelector('.sticky-sizes__value');
    const title = value && value.firstChild ? value.firstChild.textContent.trim() : '';
    const text = label && label.lastChild;
    if (title && text && text.nodeType === Node.TEXT_NODE) text.textContent = title;
  }

  async #refresh() {
    const id = this.staleId;
    if (!id) return;
    this.staleId = null;
    const section = this.closest(SECTION);
    if (!section) return;
    const sectionId = section.id.replace('shopify-section-', '');
    try {
      const html = await fetchSection(sectionId, { params: { variant: id }, signal: this.nextSignal() });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      ['[data-sticky-price]', '[data-sticky-size-value]', '[data-sticky-cta]'].forEach((selector) => {
        const target = this.querySelector(selector);
        const source = doc.querySelector(selector);
        if (target && source) target.replaceChildren(...source.childNodes);
      });
    } catch (error) {
      // Afbrudt af et nyere valg er ikke en fejl. Ellers beholder baren sin
      // forrige tilstand - buy-boxens picker viser og annoncerer selv fejlen.
      if (error.name !== 'AbortError') console.warn('[sticky-buy-bar]', error);
    }
  }

  sync() {
    const visible = this.pastWatch && !this.atEnd;
    this.classList.toggle('is-visible', visible);
    // Et variantskift mens baren var skjult hentes foerst nu.
    if (visible && this.staleId) this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.#onVariantChange);
    if (this.watchIo) this.watchIo.disconnect();
    if (this.stopIo) this.stopIo.disconnect();
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
